import type { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';
import { DayPicker } from '../DayPicker';
import { InputWrapper } from '../Form/Input/styles';
import { INPUT_BORDER_WIDTH } from '../Form/styles';
import { FormControlSizes } from '../Form/types';

const FormGroupLabel = styled.span``;
export const FormGroup = styled(
  ({ label, children, help, error, disabled, size = FormControlSizes.Default, style }: FormGroupProps) => {
    return (
      <FormGroupWrapper disabled={disabled} size={size} style={style}>
        {label != null ? <FormGroupLabel>{label}</FormGroupLabel> : null}
        {children}
        {error && <ErrorMessage error={!!error}>{error}</ErrorMessage>}
        {help && !error && <HelpText>{help}</HelpText>}
      </FormGroupWrapper>
    );
  }
)``;
type FormGroupProps = {
  controlId?: string;
  disabled?: boolean;
  label?: ReactNode;
  help?: ReactNode;
  error?: string;
  tooltip?: string;
  size?: FormControlSizes;
  children?: ReactNode;
  style?: CSSProperties;
};
const HelpText = styled.div`
  color: ${({ theme }) => theme.colorTextSubtle};
  font-size: ${({ theme }) => theme.fontSizeFormHelp}rem;
  padding: ${({ theme }) => theme.spacing2}px 0;
  line-height: 1;
`;
const ErrorMessage = styled.div<{ error?: boolean }>`
  color: ${({ theme }) => theme.colorTextNegative};
  font-size: ${({ theme }) => theme.fontSizeFormHelp}rem;
  padding: ${({ theme }) => theme.spacing2}px;
  line-height: 1;
`;
type FormGroupWrapperProps = { disabled?: boolean; size?: FormControlSizes };
const FormGroupWrapper = styled.label<FormGroupWrapperProps>`
  ${({ disabled, theme, size = FormControlSizes.Default }) => `
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: baseline;

  > ${FormGroupLabel} {
    display: flex;
    flex: 0 0 4.5em;
    min-width: 4.5em;
    transition: color 200ms ease;
    font-size: ${(theme.fontSizeDefault * size) / 2}rem;
    color: ${disabled ? theme.colorTextSubtle : 'inherit'};
  }

  > ${InputWrapper} {
    flex: 1 1 75px;
    max-width: 10em;
    line-height: ${theme.baseSize * size - INPUT_BORDER_WIDTH * 2}px;
    height: ${theme.baseSize * size}px;
    font-size: ${(theme.fontSizeDefault * size) / 2}rem;
  }

  &:focus-within {
    > ${FormGroupLabel} {
      color: ${theme.colorTextImportant};
    }
  }

  > ${ErrorMessage} {
    height: ${(theme.spacing16 * size) / 2}px;
    font-size: ${(theme.fontSizeFormHelp * size) / 2}rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    position: absolute;
    left: 0;
    right: 0;
  }

  > ${HelpText} {
    height: ${theme.spacing16}px;
    font-size: ${(theme.fontSizeFormHelp * size) / 2}rem;
    position: absolute;
    left: 0;
    right: 0;

    color: ${disabled ? theme.colorTextMuted : ''};
  }
`}
`;
export const ShortcutsHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 1;
  font-size: ${({ theme }) => theme.fontSizeSmall}em;
  color: ${({ theme }) => theme.colorTextDefault};
  margin: ${({ theme }) => `${theme.spacing4}px`} 0;
  &:before,
  &:after {
    content: '';
    flex-grow: 1;
    height: 1px;
    ${({ theme }) => `background: ${theme.borderColorInput};`}
  }
  &:before {
    ${({ theme }) => `margin-right: ${theme.spacing4}px;`}
  }
  &:after {
    ${({ theme }) => `margin-left: ${theme.spacing4}px;`}
  }
`;
export const DateTimeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  flex: 1;
  padding: ${({ theme }) => theme.spacing12}px;

  ${DayPicker} {
    padding: 0;
  }
`;
export const ShortcutButtons = styled.div`
  display: grid;
  grid-gap: ${({ theme }) => theme.spacing2}px;
  grid-template-columns: auto auto auto;
`;
export const DurationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: ${({ theme }) => theme.spacing12}px;
`;
export const PopoverWrapper = styled.div`
  display: flex;
  min-height: 18em;
  margin: -${({ theme }) => theme.spacing8}px;
  gap: ${({ theme }) => theme.spacing12}px;
`;

export const TimePickerWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing4}px;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing8}px;
`;
export const InputOverlay = styled.div<{ size?: FormControlSizes; warning?: boolean }>`
  position: absolute;
  left: ${({ theme, warning, size = FormControlSizes.Default }) =>
    ((theme.spacing8 + (warning ? theme.spacing12 : 0)) * size) / 2}px;
`;
export const DurationDescription = styled.span<{ disabled?: boolean }>`
  ${({ theme, disabled }) => `color: ${disabled ? theme.colorTextSubtle : theme.colorTextImportant};`}
`;
export const DateDescription = styled.span<{ disabled?: boolean }>`
  ${({ theme, disabled }) => `color: ${disabled ? theme.colorTextSubtle : theme.colorTextImportant};`}
  flex: 0 0 auto;
  white-space: nowrap;
  ${DurationDescription}~& {
    ${({ theme, disabled }) => `color: ${disabled ? theme.colorTextMuted : theme.colorTextSubtle};`}
  }
`;
export const DateTimeDurationPickerWrapper = styled.div<{ focused: boolean }>`
  position: relative;

  ${({ focused }) =>
    focused
      ? `
    ${InputOverlay} {
      display: none;
    }`
      : `
  ${InputOverlay} {
    display: flex;
  }
  > ${InputWrapper} > input {
    color: transparent;
  }
`}
`;
